/**
 * Frontend Encryption Utilities
 * 
 * Verschlüsselung erfolgt jetzt serverseitig über die Cloud Function
 * encryptEmailPasswordServer. Der Encryption Key ist NICHT mehr im
 * Frontend-Bundle sichtbar.
 */

import { authenticatedFetch, getCloudFunctionUrl } from './cloudFunctions';

/**
 * Verschlüsselt ein Passwort über die Backend Cloud Function.
 * Der Encryption Key bleibt serverseitig und ist nie im Browser sichtbar.
 * 
 * @param {string} password - Klartext-Passwort
 * @returns {Promise<string>} - Verschlüsseltes Passwort (Base64)
 */
export const encryptEmailPassword = async (password) => {
  if (!password || typeof password !== 'string') {
    throw new Error('Invalid password provided');
  }

  // Bereits verschlüsselt? Nicht nochmal verschlüsseln
  if (isPasswordEncrypted(password)) {
    return password;
  }

  const response = await authenticatedFetch(getCloudFunctionUrl('encryptEmailPasswordServer'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to encrypt password');
  }

  const data = await response.json();
  return data.encrypted;
};

/**
 * Prüft ob ein Passwort bereits verschlüsselt ist.
 * Einfache heuristische Prüfung basierend auf Base64-Format und Mindestlänge.
 * 
 * @param {string} password - Zu prüfendes Passwort
 * @returns {boolean}
 */
export const isPasswordEncrypted = (password) => {
  if (!password || typeof password !== 'string') return false;
  
  try {
    const decoded = atob(password);
    return decoded.length >= 28; // 12 bytes IV + min 16 bytes encrypted data
  } catch (e) {
    return false;
  }
}; 